
import { WorldCity } from '../../types';

export interface CityNamePatterns {
  prefixes: string[];
  suffixes: string[];
  geographical: string[];
  historical: string[];
  religious: string[];
  cultural: string[];
}

export interface CountryNamingData {
  patterns: CityNamePatterns;
  populationRange: { min: number; max: number };
  commonWords: string[];
  separators: string[];
}

// Base de datos completa de patrones de nomenclatura por país/región
export const countryNamingDatabase: Record<string, CountryNamingData> = {
  // Europa
  'spain': {
    patterns: {
      prefixes: ['San', 'Santa', 'Villa', 'Pueblo', 'Ciudad', 'Puerto', 'Monte', 'Valle', 'Sierra', 'Fuente'],
      suffixes: ['del Mar', 'de la Cruz', 'Real', 'Nueva', 'Alta', 'Baja', 'Mayor', 'Menor', 'Grande', 'Chica'],
      geographical: ['Rio', 'Sierra', 'Monte', 'Valle', 'Costa', 'Playa', 'Cerro', 'Loma', 'Colina', 'Prado'],
      historical: ['del Rey', 'de León', 'de Castilla', 'Imperial', 'Antigua', 'Medieval', 'Histórica'],
      religious: ['del Santo', 'de la Virgen', 'del Cristo', 'Sagrada', 'Bendita', 'Divina'],
      cultural: ['Flamenca', 'Andaluza', 'Catalana', 'Gallega', 'Vasca', 'Aragonesa', 'Extremeña']
    },
    populationRange: { min: 8000, max: 450000 },
    commonWords: ['de', 'del', 'la', 'las', 'los', 'y'],
    separators: [' de ', ' del ', ' de la ', ' y ']
  },
  'france': {
    patterns: {
      prefixes: ['Saint', 'Sainte', 'Notre-Dame', 'Le', 'La', 'Les', 'Mont', 'Pont', 'Bourg', 'Ville'],
      suffixes: ['sur-Seine', 'les-Bains', 'en-Provence', 'sur-Mer', 'la-Grande', 'le-Petit', 'du-Nord', 'du-Sud'],
      geographical: ['Rivière', 'Montagne', 'Vallée', 'Côte', 'Forêt', 'Lac', 'Colline', 'Plateau'],
      historical: ['Royal', 'Impérial', 'Ancien', 'Médiéval', 'Renaissance', 'Révolutionnaire'],
      religious: ['Abbaye', 'Cathédrale', 'Chapelle', 'Monastère', 'Basilique', 'Sanctuaire'],
      cultural: ['Bretonne', 'Normande', 'Provençale', 'Alsacienne', 'Bourguignonne', 'Corse']
    },
    populationRange: { min: 12000, max: 380000 },
    commonWords: ['de', 'du', 'des', 'le', 'la', 'les', 'sur', 'en'],
    separators: ['-', '-sur-', '-en-', '-du-', '-de-']
  },
  'germany': {
    patterns: {
      prefixes: ['Neu', 'Alt', 'Ober', 'Unter', 'Nord', 'Süd', 'Ost', 'West', 'Berg', 'Tal'],
      suffixes: ['burg', 'stadt', 'dorf', 'hausen', 'ingen', 'bach', 'feld', 'heim', 'furt', 'stein'],
      geographical: ['Berg', 'Tal', 'Wald', 'See', 'Fluss', 'Hügel', 'Ebene', 'Heide'],
      historical: ['Kaiser', 'König', 'Herzog', 'Graf', 'Ritter', 'Fürst', 'Reich'],
      religious: ['Kloster', 'Kirche', 'Dom', 'Stift', 'Heilig', 'Sankt'],
      cultural: ['Bayer', 'Schwab', 'Preuß', 'Sächs', 'Hess', 'Thüring']
    },
    populationRange: { min: 15000, max: 420000 },
    commonWords: ['am', 'an', 'auf', 'bei', 'im', 'zu', 'von'],
    separators: ['', 'am', 'an', 'bei', 'zu']
  },
  'italy': {
    patterns: {
      prefixes: ['San', 'Santa', 'Monte', 'Castel', 'Villa', 'Borgo', 'Porto', 'Torre', 'Rocca', 'Fonte'],
      suffixes: ['del Mare', 'della Pace', 'Nuovo', 'Vecchio', 'Alto', 'Basso', 'Grande', 'Piccolo'],
      geographical: ['Monte', 'Valle', 'Costa', 'Lago', 'Fiume', 'Collina', 'Piano', 'Marina'],
      historical: ['Romano', 'Antico', 'Imperiale', 'Medievale', 'Rinascimentale', 'Pontificio'],
      religious: ['Benedetto', 'Francesco', 'Giuseppe', 'Maria', 'Angelo', 'Spirito'],
      cultural: ['Toscano', 'Siciliano', 'Napoletano', 'Veneto', 'Lombardo', 'Piemontese']
    },
    populationRange: { min: 10000, max: 390000 },
    commonWords: ['del', 'della', 'di', 'da', 'in', 'sul', 'sulla'],
    separators: [' del ', ' della ', ' di ', ' da ']
  },
  // Asia
  'turkey': {
    patterns: {
      prefixes: ['Yeni', 'Eski', 'Büyük', 'Küçük', 'Orta', 'Doğu', 'Batı', 'Kuzey', 'Güney', 'İç'],
      suffixes: ['köy', 'şehir', 'kent', 'abad', 'tepe', 'dere', 'ova', 'beli', 'burnu', 'adası'],
      geographical: ['Dağ', 'Göl', 'Nehir', 'Deniz', 'Vadi', 'Yayla', 'Boğaz', 'Koy'],
      historical: ['Sultan', 'Paşa', 'Bey', 'Aga', 'Efendi', 'Hanım', 'Kral', 'İmparator'],
      religious: ['Cami', 'Türbe', 'Tekke', 'Zaviye', 'Medrese', 'İmaret', 'Vakıf'],
      cultural: ['Türk', 'Osmanlı', 'Anadolu', 'Rum', 'Selçuk', 'Bizans']
    },
    populationRange: { min: 12000, max: 400000 },
    commonWords: ['da', 'de', 'ta', 'te', 'dan', 'den', 'tan', 'ten'],
    separators: ['', ' ']
  },
  'iran': {
    patterns: {
      prefixes: ['Shahre', 'Qaleh', 'Bandar', 'Kuh', 'Dasht', 'Abad', 'Shahr', 'Deh', 'Qari', 'Mir'],
      suffixes: ['abad', 'shahr', 'kand', 'gah', 'stan', 'pur', 'gun', 'dar', 'var', 'bad'],
      geographical: ['Kuh', 'Dasht', 'Darya', 'Rud', 'Kavir', 'Tang', 'Sabz', 'Siah'],
      historical: ['Shah', 'Malik', 'Mirza', 'Khan', 'Beg', 'Agha', 'Soltan', 'Emir'],
      religious: ['Emam', 'Seyed', 'Haj', 'Mashhad', 'Qom', 'Najaf', 'Karbala'],
      cultural: ['Fars', 'Azari', 'Kurd', 'Baluch', 'Turkmen', 'Arab', 'Lor']
    },
    populationRange: { min: 8000, max: 380000 },
    commonWords: ['e', 'ye', 'ha', 'an', 'at', 'el'],
    separators: [' e ', ' ye ', '', '-']
  },
  // África
  'morocco': {
    patterns: {
      prefixes: ['Sidi', 'Oulad', 'Beni', 'Ain', 'Ksar', 'Douar', 'Souk', 'Ait', 'Immi', 'Tizi'],
      suffixes: ['ville', 'douar', 'ksar', 'souk', 'mellah', 'medina', 'jadida', 'qdima'],
      geographical: ['Jebel', 'Oued', 'Sahel', 'Rif', 'Atlas', 'Tizi', 'Adrar', 'Azrou'],
      historical: ['Moulay', 'Lalla', 'Sultan', 'Calif', 'Emir', 'Cherif', 'Imam'],
      religious: ['Zawiya', 'Mosque', 'Marabout', 'Sidi', 'Moulay', 'Lalla'],
      cultural: ['Amazigh', 'Arab', 'Andalusi', 'Sahrawi', 'Rifain', 'Sousi']
    },
    populationRange: { min: 7000, max: 350000 },
    commonWords: ['ben', 'ait', 'ould', 'lalla', 'sidi'],
    separators: [' ', '-', ' ben ', ' ait ']
  },
  'algeria': {
    patterns: {
      prefixes: ['Sidi', 'Ouled', 'Beni', 'Ain', 'Bir', 'El', 'Ech', 'Ksar', 'Bordj', 'Reggan'],
      suffixes: ['ville', 'douar', 'ksar', 'souk', 'oued', 'djebel', 'sahel', 'chott'],
      geographical: ['Djebel', 'Oued', 'Chott', 'Sebkha', 'Erg', 'Hamada', 'Reg'],
      historical: ['Emir', 'Bey', 'Dey', 'Agha', 'Caid', 'Sheikh', 'Marabout'],
      religious: ['Zawiya', 'Koubba', 'Sidi', 'Moulay', 'Lalla', 'Masjid'],
      cultural: ['Kabyle', 'Chaoui', 'Mozabite', 'Touareg', 'Arabe', 'Berbere']
    },
    populationRange: { min: 9000, max: 370000 },
    commonWords: ['ben', 'ould', 'el', 'ech', 'es'],
    separators: [' ', '-', ' el ', ' ech ']
  },
  // Asia Central
  'uzbekistan': {
    patterns: {
      prefixes: ['Yangi', 'Eski', 'Katta', 'Kichik', 'Sharq', 'G\'arb', 'Shimol', 'Janub', 'O\'rta', 'Yuqori'],
      suffixes: ['abad', 'kent', 'qala', 'tepa', 'bog', 'kul', 'daryo', 'soy', 'ota', 'ona'],
      geographical: ['Tog\'', 'Daryo', 'Ko\'l', 'Vodiy', 'Cho\'l', 'Tepa', 'Soy', 'Bulok'],
      historical: ['Amir', 'Khan', 'Bek', 'Mirzo', 'Podshoh', 'Sulton', 'Xoqon'],
      religious: ['Masjid', 'Madrasah', 'Xonaqoh', 'Maqbara', 'Ziyorat', 'Mavlono'],
      cultural: ['O\'zbek', 'Qozoq', 'Qirg\'iz', 'Tojik', 'Turkman', 'Qoraqalpoq']
    },
    populationRange: { min: 8000, max: 320000 },
    commonWords: ['ning', 'dan', 'ga', 'da', 'ni', 'si'],
    separators: ['', '-', ' ']
  },
  // Europa del Este
  'romania': {
    patterns: {
      prefixes: ['Noul', 'Vechiul', 'Mare', 'Mic', 'Nord', 'Sud', 'Est', 'Vest', 'Sus', 'Jos'],
      suffixes: ['esti', 'eni', 'ani', 'oaia', 'ul', 'ea', 'ii', 'ele', 'ilor', 'ului'],
      geographical: ['Munte', 'Vale', 'Râu', 'Lac', 'Deal', 'Câmp', 'Pădure', 'Deltă'],
      historical: ['Domnesc', 'Regal', 'Boieresc', 'Voievodal', 'Princiar', 'Noble'],
      religious: ['Sfânt', 'Sfânta', 'Mănăstire', 'Biserica', 'Catedrala', 'Schit'],
      cultural: ['Moldovenesc', 'Ardelenesc', 'Oltenesc', 'Dobrogean', 'Bănățean']
    },
    populationRange: { min: 6000, max: 280000 },
    commonWords: ['de', 'din', 'pe', 'la', 'în', 'cu', 'sub'],
    separators: [' de ', ' din ', ' pe ', '-']
  },
  // Reino Unido
  'united-kingdom': {
    patterns: {
      prefixes: ['New', 'Old', 'Great', 'Little', 'North', 'South', 'East', 'West', 'Upper', 'Lower'],
      suffixes: ['ton', 'ham', 'shire', 'ford', 'bury', 'field', 'wood', 'dale', 'bridge', 'port'],
      geographical: ['Hill', 'Valley', 'River', 'Lake', 'Moor', 'Heath', 'Down', 'Common'],
      historical: ['Royal', 'King', 'Queen', 'Prince', 'Duke', 'Earl', 'Lord', 'Castle'],
      religious: ['Saint', 'Abbey', 'Minster', 'Cathedral', 'Chapel', 'Priory'],
      cultural: ['English', 'Scottish', 'Welsh', 'Irish', 'Norman', 'Saxon', 'Celtic']
    },
    populationRange: { min: 12000, max: 450000 },
    commonWords: ['upon', 'under', 'by', 'on', 'in', 'at', 'near'],
    separators: ['-', ' upon ', ' under ', ' by ']
  },
  // Patrón por defecto para países no específicos
  'default': {
    patterns: {
      prefixes: ['New', 'San', 'Saint', 'North', 'South', 'East', 'West', 'Upper', 'Lower', 'Grand'],
      suffixes: ['ville', 'town', 'city', 'burg', 'field', 'wood', 'dale', 'ford', 'port', 'haven'],
      geographical: ['River', 'Lake', 'Hill', 'Valley', 'Mountain', 'Bay', 'Coast', 'Ridge'],
      historical: ['Royal', 'Imperial', 'Ancient', 'Old', 'Historic', 'Noble', 'Majestic'],
      religious: ['Sacred', 'Holy', 'Blessed', 'Divine', 'Saint', 'Angel', 'Spirit'],
      cultural: ['Cultural', 'Traditional', 'Heritage', 'Classic', 'Native', 'Local']
    },
    populationRange: { min: 10000, max: 400000 },
    commonWords: ['of', 'de', 'del', 'da', 'von', 'van', 'el'],
    separators: [' ', '-', ' of ', ' de ']
  }
};

/**
 * Genera nombres de ciudades realistas y únicos para un país específico
 */
export const generateRealisticCityNames = (
  countrySlug: string, 
  count: number, 
  startIndex: number = 1,
  existingNames: Set<string> = new Set()
): WorldCity[] => {
  const namingData = countryNamingDatabase[countrySlug] || countryNamingDatabase.default;
  const cities: WorldCity[] = [];
  const usedNames = new Set(existingNames);
  const usedSlugs = new Set<string>();
  
  let attempts = 0;
  const maxAttempts = count * 15; // Aumentamos los intentos para generar más variedad
  
  while (cities.length < count && attempts < maxAttempts) {
    attempts++;
    
    const cityName = generateSingleCityName(namingData, startIndex + cities.length);
    const citySlug = generateSlug(cityName);
    
    // Verificar duplicados
    if (!usedNames.has(cityName) && !usedSlugs.has(citySlug)) {
      const population = Math.floor(
        Math.random() * (namingData.populationRange.max - namingData.populationRange.min) + 
        namingData.populationRange.min
      );
      
      cities.push({
        name: cityName,
        slug: citySlug,
        population
      });
      
      usedNames.add(cityName);
      usedSlugs.add(citySlug);
    }
  }
  
  if (cities.length < count) {
    console.warn(`⚠️ Solo se pudieron generar ${cities.length}/${count} ciudades únicas para ${countrySlug}`);
  }
  
  return cities;
};

/**
 * Genera un nombre de ciudad individual usando patrones realistas
 */
const generateSingleCityName = (namingData: CountryNamingData, index: number): string => {
  const { patterns, separators } = namingData;
  const nameTypes = ['prefix-suffix', 'geographical', 'historical-geographical', 'religious', 'cultural', 'simple'];
  const nameType = nameTypes[Math.floor(Math.random() * nameTypes.length)];
  
  let name = '';
  
  switch (nameType) {
    case 'prefix-suffix':
      const prefix = patterns.prefixes[Math.floor(Math.random() * patterns.prefixes.length)];
      const suffix = patterns.suffixes[Math.floor(Math.random() * patterns.suffixes.length)];
      const separator = separators[Math.floor(Math.random() * separators.length)];
      name = `${prefix}${separator}${suffix}`;
      break;
      
    case 'geographical':
      const geo1 = patterns.geographical[Math.floor(Math.random() * patterns.geographical.length)];
      const geo2 = patterns.suffixes[Math.floor(Math.random() * patterns.suffixes.length)];
      name = `${geo1} ${geo2}`;
      break;
      
    case 'historical-geographical':
      const hist = patterns.historical[Math.floor(Math.random() * patterns.historical.length)];
      const geo = patterns.geographical[Math.floor(Math.random() * patterns.geographical.length)];
      name = `${hist} ${geo}`;
      break;
      
    case 'religious':
      const rel = patterns.religious[Math.floor(Math.random() * patterns.religious.length)];
      const suf = patterns.suffixes[Math.floor(Math.random() * patterns.suffixes.length)];
      name = `${rel} ${suf}`;
      break;
      
    case 'cultural':
      const cult = patterns.cultural[Math.floor(Math.random() * patterns.cultural.length)];
      const pre = patterns.prefixes[Math.floor(Math.random() * patterns.prefixes.length)];
      name = `${pre} ${cult}`;
      break;
      
    case 'simple':
      // Solo usar prefijos o sufijos simples sin números
      const simple = Math.random() < 0.5 ? 
        patterns.prefixes[Math.floor(Math.random() * patterns.prefixes.length)] :
        patterns.suffixes[Math.floor(Math.random() * patterns.suffixes.length)];
      name = simple;
      break;
      
    default:
      // Fallback
      const randomPrefix = patterns.prefixes[Math.floor(Math.random() * patterns.prefixes.length)];
      const randomSuffix = patterns.suffixes[Math.floor(Math.random() * patterns.suffixes.length)];
      name = `${randomPrefix} ${randomSuffix}`;
  }
  
  // REMOVIDO: No añadir números al final del nombre
  // if (Math.random() < 0.3) { 
  //   name += ` ${Math.floor(Math.random() * 99) + 1}`;
  // }
  
  return name.trim();
};

/**
 * Genera un slug válido para URL desde un nombre de ciudad
 */
const generateSlug = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[áàäâãå]/g, 'a')
    .replace(/[éèëê]/g, 'e')
    .replace(/[íìïî]/g, 'i')
    .replace(/[óòöôõø]/g, 'o')
    .replace(/[úùüû]/g, 'u')
    .replace(/[ýÿ]/g, 'y')
    .replace(/[ñ]/g, 'n')
    .replace(/[ç]/g, 'c')
    .replace(/[ğ]/g, 'g')
    .replace(/[ı]/g, 'i')
    .replace(/[ö]/g, 'o')
    .replace(/[ş]/g, 's')
    .replace(/[ü]/g, 'u')
    .replace(/[ț]/g, 't')
    .replace(/[ș]/g, 's')
    .replace(/[ă]/g, 'a')
    .replace(/[â]/g, 'a')
    .replace(/[î]/g, 'i')
    .replace(/['/\s]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

/**
 * Valida que un nombre de ciudad sea único dentro de un conjunto
 */
export const validateUniqueness = (
  cityName: string, 
  citySlug: string, 
  existingCities: WorldCity[]
): boolean => {
  return !existingCities.some(city => 
    city.name.toLowerCase() === cityName.toLowerCase() || 
    city.slug === citySlug
  );
};
